import type { AtAGlanceObservationSnapshot } from './atAGlanceObservation.ts';
import type { AtAGlancePanelFields } from './atAGlancePanelObservation.ts';

type WaitKind = 'inputWaitMs' | 'retryWaitMs' | 'responseWaitMs' | 'unknownWaitMs';

export type AtAGlancePanelWait = {
	getOpenedAt: () => number | undefined;
	start: () => void;
	account: (open: boolean, documentReady: boolean | undefined) => void;
	observeRequest: (snapshot: AtAGlanceObservationSnapshot) => void;
	overviewCommitted: () => void;
	fields: (startKnown: boolean) => AtAGlancePanelFields;
};

const getWaitKind = (snapshot: AtAGlanceObservationSnapshot): WaitKind => {
	if (snapshot.decision === 'waitingForInput') {
		return 'inputWaitMs';
	}
	if (snapshot.phase === 'retryDelay') {
		return 'retryWaitMs';
	}
	if (snapshot.phase === 'activeAttempt') {
		return 'responseWaitMs';
	}
	return 'unknownWaitMs';
};

const emptyTotals = () => ({
	inputWaitMs: 0,
	documentWaitMs: 0,
	retryWaitMs: 0,
	responseWaitMs: 0,
	unknownWaitMs: 0,
});

export const createAtAGlancePanelWait = (now: () => number): AtAGlancePanelWait => {
	let openedAt: number | undefined;
	let accountedAt: number | undefined;
	let firstOverviewAt: number | undefined;
	let committed = false;
	let timingComplete = true;
	let waitKind: WaitKind = 'unknownWaitMs';
	let totals = emptyTotals();

	const time = (): number | undefined => {
		try {
			const value = now();
			if (Number.isFinite(value) && value >= 0) {
				return value;
			}
		} catch {
			// Missing clock evidence must not interrupt the panel lifecycle.
		}
		return undefined;
	};

	const start = () => {
		openedAt = time();
		accountedAt = openedAt;
		firstOverviewAt = undefined;
		committed = false;
		timingComplete = openedAt !== undefined;
		totals = emptyTotals();
	};

	const account = (open: boolean, documentReady: boolean | undefined) => {
		const current = time();
		if (
			open &&
			!committed &&
			current !== undefined &&
			accountedAt !== undefined &&
			current >= accountedAt
		) {
			const elapsed = current - accountedAt;
			totals[waitKind] += elapsed;
			// Document wait overlaps input wait; it is not another interval to add to the total.
			if (waitKind === 'inputWaitMs' && documentReady === false) {
				totals.documentWaitMs += elapsed;
			}
		}
		if (open && (current === undefined || accountedAt === undefined || current < accountedAt)) {
			timingComplete = false;
		}
		accountedAt = current;
	};

	const fields = (startKnown: boolean): AtAGlancePanelFields => {
		const timing: AtAGlancePanelFields = {
			waitTimingCoverage: timingComplete ? 'complete' : 'partial',
			...totals,
		};
		if (
			startKnown &&
			openedAt !== undefined &&
			firstOverviewAt !== undefined &&
			firstOverviewAt >= openedAt
		) {
			timing.panelWaitMs = firstOverviewAt - openedAt;
		}
		return timing;
	};

	return {
		getOpenedAt: () => openedAt,
		start,
		account,
		observeRequest: (snapshot: AtAGlanceObservationSnapshot) => {
			waitKind = getWaitKind(snapshot);
		},
		overviewCommitted: () => {
			committed = true;
			firstOverviewAt = time();
		},
		fields,
	};
};
